import React, { useState, useEffect } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";

const IncidentReportsList = ({ records = [], onAdd, onEdit, onDelete }) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredRecords, setFilteredRecords] = useState(records);

  // ✅ Update filtered data when records or filterText change
  useEffect(() => {
    const filtered = records.filter((r) =>
      Object.values(r || {}).some((v) =>
        String(v || "").toLowerCase().includes(filterText.toLowerCase())
      )
    );
    setFilteredRecords(filtered);
    setCurrentPage(1);
  }, [records, filterText]);

  const totalRows = filteredRecords.length;
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  // ✅ Delete confirmation
  const handleDelete = (id) => {
    Swal.fire({
      title: "Confirm Delete",
      text: "Do you want to delete this incident report?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed && onDelete) {
        onDelete(id);
      }
    });
  };

  // ✅ PDF Export
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Incident Reports", 14, 10);
    const tableColumn = [
      "#",
      "Client Name",
      "Room No",
      "Date",
      "Time",
      "Location",
      "Incident Type",
      "Description",
      "Action Taken",
      "Reported By",
      "Head Nurse",
      "Follow-Up",
      "Created By",
      "Updated By",
    ];

    const tableRows = filteredRecords.map((r, i) => [
      i + 1,
      r.client_name || "-",
      r.room_number || "-",
      r.date || "-",
      r.time || "-",
      r.location || "-",
      r.incident_type || "-",
      r.description || "-",
      r.action_taken || "-",
      r.reported_by || "-",
      r.head_nurse || "-",
      r.follow_up_required ? "Yes" : "No",
      r.created_by || "-",
      r.updated_by || "-",
    ]);

    autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
    doc.save("incident_reports.pdf");
  };

  // ✅ Custom pagination UI
  const CustomPagination = ({
    onChangePage,
    onChangeRowsPerPage,
    currentPage,
    rowsPerPage,
    rowCount,
  }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
            Rows per page:
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={rowsPerPage}
            onChange={(e) =>
              onChangeRowsPerPage(Number(e.target.value), currentPage)
            }
          >
            {[5, 10, 15, 20].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm border"
            onClick={() => onChangePage(currentPage - 1, rowCount)}
            disabled={currentPage === 1}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-sm border"
            onClick={() => onChangePage(currentPage + 1, rowCount)}
            disabled={currentPage === totalPages}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  // ✅ Table Columns
  const columns = [
    {
      name: "S.No",
      cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
      width: "70px",
    },
    {
      name: "Client(s)",
      selector: (r) => r.clients?.map((c) => c.name).join(", ") || "-",
      sortable: true,
    },
    {
      name: "Reported By",
      selector: (r) => r.report_completed_by || "-",
      sortable: true,
    },
    {
      name: "Date of Report",
      selector: (r) =>
        r.date_of_report ? new Date(r.date_of_report).toLocaleDateString() : "-",
      sortable: true,
    },
    // {
    //   name: "Incident Date & Time",
    //   selector: (r) =>
    //     r.incident_date_time
    //       ? new Date(r.incident_date_time).toLocaleString()
    //       : "-",
    //   sortable: true,
    // },
    {
      name: "Location",
      selector: (r) => r.place_of_incident || "-",
      sortable: true,
    },
    // {
    //   name: "Nature of Incident",
    //   selector: (r) =>
    //     r.nature_of_incident === "Other"
    //       ? `${r.nature_of_incident} (${r.other_nature})`
    //       : r.nature_of_incident || "-",
    //   sortable: true,
    // },
    {
      name: "Summary",
      selector: (r) => r.summary || "-",
      sortable: true,
    },
    {
      name: "Action Taken",
      selector: (r) => r.action_taken || "-",
      sortable: true,
    },
    {
      name: "Follow-up Necessary",
      selector: (r) => r.follow_up_necessary || "-",
      sortable: true,
    },
    {
      name: "Follow-up Details",
      selector: (r) => r.follow_up_necessary_details || "-",
      sortable: true,
    },
    {
      name: "Reported to Trustees",
      selector: (r) => r.action_reported_to_trustees || "-",
      sortable: true,
    },
    {
      name: "Created By",
      selector: (r) => r.creator?.name || "-",
      sortable: true,
    },
    {
      name: "Updated By",
      selector: (r) => r.updater?.name || "-",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-1">
          <button
            className="btn btn-sm btn-outline-primary"
            title="Edit"
            onClick={() => onEdit && onEdit(row.id)}
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            title="Delete"
            onClick={() => handleDelete(row.id)}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
      width: "130px",
      ignoreRowClick: true,
      button: true,
    },
  ];

  return (
    <div>
      {/* === HEADER & FILTER BAR === */}
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        {/* Export + Search */}
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button
              className="btn btn-label-primary btn-sm dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li>
                <button className="dropdown-item" onClick={handleExportPDF}>
                  <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                </button>
              </li>
              <li>
                <CSVLink
                  data={filteredRecords}
                  filename="incident_reports.csv"
                  className="dropdown-item"
                >
                  <i className="ri-file-excel-line me-1 text-success"></i> CSV
                </CSVLink>
              </li>
            </ul>
          </div>

          <input
            type="text"
            placeholder="Search Incident Reports..."
            className="form-control form-control-sm"
            style={{ width: "220px" }}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <h6 className="mb-0 text-center text-muted flex-grow-1">
          Showing {start}-{end} of {totalRows} records
        </h6>

        <div>
          {onAdd && (
            <button className="btn btn-sm btn-primary" onClick={onAdd}>
              <i className="ri-add-line me-1"></i> Add New
            </button>
          )}
        </div>
      </div>

      {/* === DATA TABLE === */}
      <div className="card-body p-2">
        <DataTable
          columns={columns}
          data={filteredRecords}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          noDataComponent={
            <div className="p-3 text-muted">No incident reports found</div>
          }
        />
      </div>
    </div>
  );
};

export default IncidentReportsList;
